import {LoaderFunctionArgs, RouteObject} from 'react-router';
import {loadHelpArticles} from '../../help-center/articleStore';
import {parseHelpLocaleFromPath} from '../../help-center/locales';

// Each help content route's loader fetches its locale's articles chunk (#366)
// alongside the screen chunk, so the screen renders with its articles already
// there: no loading flash, no transient "article not found", and the
// prerenderer never captures a half-loaded page. On the content routes, not
// the `help` layout: the locale is a static path segment, not a param, so a
// layout loader would not re-run on a /help -> /help/fr navigation.
//
// The locale comes from the URL exactly as the hooks parse it
// (useHelpContentLocale), not from the route branch: React Router matches
// paths case-insensitively, so /help/FR/… lands on the `fr` routes while the
// hooks read it as English — loading `fr` there would leave them nothing.
//
// A first visit on a bare URL by a fr/de/it/es visitor loads English here,
// then the layout's one-off auto-redirect loads the visitor's locale: one
// extra chunk, once per session (the mobile app's deep links take this path).
const loader = async ({request}: LoaderFunctionArgs) => {
  await loadHelpArticles(
    parseHelpLocaleFromPath(new URL(request.url).pathname) ?? 'en',
  );
  return null;
};

// The help content routes exist once per locale: at `/help` (English) and
// under each `/help/<locale>` prefix. Fresh objects per call — route objects
// must not be shared between branches of the tree.
export const helpContentRoutes = (): RouteObject[] => [
  {
    path: '',
    loader,
    lazy: () => import('../../screens/HelpCenter/ArticleListScreen'),
  },
  {
    path: 'category/:category',
    loader,
    lazy: () => import('../../screens/HelpCenter/CategoryScreen'),
  },
  {
    path: ':slug',
    loader,
    lazy: () => import('../../screens/HelpCenter/ArticleViewScreen'),
  },
];
